import { FC, useContext, useEffect, useState } from "react";
import { getData } from "../../flows/backends/AutologServicesBackend";
import AppContext from "../../../contexts/AppContext";
import ModalDialog from "../../../components/common/ModalDialog";
import { useApi } from "../../../future/hooks/useApi";
import { ApiRequest } from "../../../future/api";
import { FlowModel, FlowStepModel } from "../../flows/models";

type DataRow = {
  id: number;
  name: string;
  regdate: string;
  info: {
    casenr: string;
    Aisummary: string;
    Aicategory: string;
    Aiexplanation: string;
  };
  raw_data?: any[];
};

type ExportField = {
  name: string;
  getValue: (row: DataRow) => any;
};

type FieldId = {
  stepId: string;
  type: "in" | "out";
  fieldId: string;
  fieldName: string;
};

const initialExportFields: { name: string; getValue: (row: DataRow) => any }[] =
  [
    { name: "Id", getValue: (row: DataRow) => row.id },
    { name: "Flow", getValue: (row: DataRow) => row.name },
    { name: "Date", getValue: (row: DataRow) => row.regdate?.split("T")[0] },
    { name: "Casenr", getValue: (row: DataRow) => row.info?.casenr },
    { name: "AISummary", getValue: (row: DataRow) => row.info?.Aisummary },
    { name: "AICategory", getValue: (row: DataRow) => row.info?.Aicategory },
    {
      name: "AIExplanation",
      getValue: (row: DataRow) => row.info?.Aiexplanation,
    },
  ];

const defaultFields = [
  "Id",
  "Flow",
  "Date",
  "Casenr",
  "AISummary",
  "AICategory",
  "AIExplanation",
];

const downloadCSV = (exportFields: ExportField[], data: DataRow[]) => {
  let csvContent = exportFields.map((f) => f.name).join(";") + "\n";
  csvContent += data
    .map((row: DataRow) => exportFields.map((ef) => ef.getValue(row)).join(";"))
    .join("\n"); // Convert rows to CSV format

  // Create a Blob from the CSV content
  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = URL.createObjectURL(blob);

  // Create a temporary link and trigger download
  const a = document.createElement("a");
  a.href = url;
  a.download = "data.csv";
  a.click();

  // Clean up
  URL.revokeObjectURL(url);
};

export const ExportToCsvButton: FC<{
  startDate: string;
  endDate: string;
  flowGroupId?: number;
}> = (props) => {
  const appContext = useContext(AppContext);
  const api = useApi("AutologProd");

  const [dialog, setDialog] = useState<boolean>(false);
  const [flowData, setFlowData] = useState<FlowModel>();
  const [customExportFields, setCustomExportFields] = useState<FieldId[]>([]);

  async function exportData() {
    const data = await getData(
      `autolog/export/flows?startDate=${props.startDate}&endDate=${
        props.endDate
      }${props.flowGroupId ? "&flowGroupId=" + props.flowGroupId : ""}`,
      appContext.login?.uid
    );
    const exportFields: ExportField[] = [...initialExportFields];
    customExportFields.forEach((field) => {
      exportFields.push({
        name: field.fieldName,
        getValue: (row: DataRow) =>
          row.raw_data?.find((r) => r.status === field.stepId)?.[field.type]?.[
            field.fieldId
          ] || "",
      });
    });

    downloadCSV(exportFields, data);
  }

  async function getFlowData(flowId: number) {
    const data = await api.process(new ApiRequest(`flows/${flowId}`, "GET"));
    setFlowData(data as FlowModel);
  }

  useEffect(() => {
    if (!dialog) {
      setCustomExportFields([]);
    }
  }, [dialog]);

  return (
    <>
      <ModalDialog flex visible={dialog} onClose={() => setDialog(false)}>
        <div className="flex gap-4">
          <div>
            <div className="text-white border-b border-white mb-4 pb-2">
              {initialExportFields.map((item) => (
                <p className="flex gap-2">
                  <input type="checkbox" checked />
                  <label>{item.name}</label>
                </p>
              ))}
              {customExportFields.map((item) => (
                <p className="flex gap-2">
                  <input type="checkbox" checked />
                  <label>{item.fieldName}</label>
                </p>
              ))}
            </div>
            <div className="text-white">
              {api.isProcessing
                ? "Loading..."
                : flowData?.steps &&
                  flowData?.steps?.map((step) => (
                    <FlowStepPanel
                      key={step.id}
                      flowStep={step}
                      onCheckedChanged={(checked, field) => {
                        if (checked) {
                          setCustomExportFields([...customExportFields, field]);
                        } else {
                          setCustomExportFields(
                            customExportFields.filter(
                              (f) =>
                                !(
                                  f.stepId === field.stepId &&
                                  f.type === field.type &&
                                  f.fieldId === field.fieldId
                                )
                            )
                          );
                        }
                      }}
                    />
                  ))}
            </div>
          </div>
          <div>
            <button
              className="text-white border-white border-2 rounded-md px-4 py-2 mt-auto self-end"
              onClick={() => {
                setDialog(false);
                exportData();
              }}
            >
              Export
            </button>
          </div>
        </div>
      </ModalDialog>
      <button
        className="flex"
        onClick={() => {
          if (!props.flowGroupId) {
            exportData();
            return;
          }
          setDialog(true);
          getFlowData(props.flowGroupId || 0);
        }}
      >
        <img className="h-8" src="/assets/images/csv-export.png" />
      </button>
    </>
  );
};

const FlowStepPanel = (props: {
  flowStep: FlowStepModel;
  onCheckedChanged: (checked: boolean, fieldId: FieldId) => void;
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div>
      <h3>
        <button
          className="text-left w-4"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? "-" : "+"}
        </button>{" "}
        {props.flowStep.name}
      </h3>
      <div className={!expanded ? "hidden" : "flex pl-8 gap-8"}>
        <div>
          <h3>Inputs</h3>
          {props.flowStep.step_data.in.map((field) => (
            <div key={field.id} className="flex gap-2">
              <input
                type="checkbox"
                onChange={(e) =>
                  props.onCheckedChanged(e.currentTarget.checked, {
                    stepId: props.flowStep.status,
                    type: "in",
                    fieldId: field.id,
                    fieldName: field.name,
                  })
                }
              />
              <label>
                {field.name} ({field.type})
              </label>
            </div>
          ))}
        </div>
        <div>
          <h3>Outputs </h3>
          {props.flowStep.step_data.out.map((field) => (
            <div key={field.id} className="flex gap-2">
              <input
                type="checkbox"
                onChange={(e) =>
                  props.onCheckedChanged(e.currentTarget.checked, {
                    stepId: props.flowStep.status,
                    type: "out",
                    fieldId: field.id,
                    fieldName: field.name,
                  })
                }
              />
              <label>
                {field.name} ({field.type})
              </label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
